<template>
    <div :class="`captcha-align-${align} captcha`">
        <label for="captcha" v-if="!isSpecial" class="field-label">
            {{ fieldLabel }}
            <span v-if="isRequired" class="required-indicator">*</span>
        </label>
        
        <div :class="['captcha-container', { 'error': shouldShowError }]" v-if="!isSpecial">
            <div class="captcha-preview">
                <canvas 
                    ref="captchaCanvas"
                    :width="captchaWidth"
                    :height="captchaHeight"
                    class="captcha-canvas"
                ></canvas>
            </div>
            
            <div class="captcha-input-container">
                <input 
                    type="text"
                    id="captcha"
                    name="captcha"
                    v-model="captchaInput"
                    @input="emitValues"
                    @focus="onFocus"
                    @blur="onBlur"
                    :placeholder="placeholder || 'Yukarıdaki kodu girin'"
                    class="captcha-input"
                    maxlength="8"
                    autocomplete="off"
                />
                <button @click="refreshCaptcha" class="refresh-icon-button" type="button">
                    <svg class="refresh-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M1 4v6h6"/>
                        <path d="M23 20v-6h-6"/>
                        <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                    </svg>
                </button>
            </div>
        </div>
        
        
        <div v-if="isSpecial" class="special-captcha-wrapper">
            <div class="special-captcha-container">
                <canvas 
                    ref="specialCaptchaCanvas"
                    :width="captchaWidth"
                    :height="captchaHeight"
                    class="special-captcha-canvas"
                ></canvas>
                <div class="special-input-group">
                    <input
                        type="text"
                        id="captcha-nano"
                        name="captcha"
                        v-model="captchaInput"
                        @input="emitValues"
                        class="special-captcha-input"
                        :placeholder="fieldLabel"
                        maxlength="8"
                        autocomplete="off"
                    />
                    <button @click="refreshCaptcha" class="special-refresh-button" type="button">
                        <svg class="refresh-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M1 4v6h6"/>
                            <path d="M23 20v-6h-6"/>
                            <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "FormCaptcha",
    props: {
        fieldLabel: {
            type: String,
            required: true,
        },
        align: {
            type: String,
            default: "center",
        },
        placeholder: {
            type: String,
            default: "Yukarıdaki kodu girin",
        },
        isRequired: {
            type: Boolean,
            default: true,
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        },
        shouldShowError: {
            type: Boolean,
            default: false,
        },
        isSpecial: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            captchaInput: '',
            currentCaptchaCode: '',
            captchaWidth: 180,
            captchaHeight: 60,
            isValid: false,
        };
    },
    mounted() {
        this.generateCaptcha();
        console.log('🔴 Visitor Captcha mounted:', {
            fieldLabel: this.fieldLabel,
            formId: this.formId,
            order: this.order
        });
    },
    methods: {
        generateCaptcha() {
            const canvas = this.isSpecial ? this.$refs.specialCaptchaCanvas : this.$refs.captchaCanvas;
            if (!canvas) return;
            
            const ctx = canvas.getContext('2d');
            
            ctx.clearRect(0, 0, this.captchaWidth, this.captchaHeight);
            ctx.fillStyle = '#f8f9fa';
            ctx.fillRect(0, 0, this.captchaWidth, this.captchaHeight);
            
            this.currentCaptchaCode = this.generateCode();
            
            this.addNoise(ctx);
            
            this.drawCaptchaText(ctx, this.currentCaptchaCode);
            
            console.log('🔴 Generated visitor captcha:', this.currentCaptchaCode);
        },
        
        generateCode() {
            const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
            let code = '';
            
            for (let i = 0; i < 5; i++) {
                const randomIndex = Math.floor(Math.random() * characters.length);
                code += characters[randomIndex];
            }
            
            return code;
        },
        
        addNoise(ctx) {
            for (let i = 0; i < 60; i++) {
                ctx.fillStyle = `rgba(${Math.random() * 100}, ${Math.random() * 100}, ${Math.random() * 100}, 0.4)`;
                ctx.fillRect(Math.random() * this.captchaWidth, Math.random() * this.captchaHeight, 2, 2);
            }
            
            for (let i = 0; i < 2; i++) {
                ctx.strokeStyle = `rgba(${Math.random() * 150}, ${Math.random() * 150}, ${Math.random() * 150}, 0.5)`;
                ctx.lineWidth = Math.random() * 2 + 1;
                ctx.beginPath();
                ctx.moveTo(Math.random() * this.captchaWidth, Math.random() * this.captchaHeight);
                ctx.lineTo(Math.random() * this.captchaWidth, Math.random() * this.captchaHeight);
                ctx.stroke();
            }
        },
        
        drawCaptchaText(ctx, code) {
            const fontSize = 26;
            ctx.font = `bold ${fontSize}px Arial, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            
            const spacing = this.captchaWidth / (code.length + 1);
            
            for (let i = 0; i < code.length; i++) {
                const char = code[i];
                const x = spacing * (i + 1) + (Math.random() - 0.5) * 6;
                const y = this.captchaHeight / 2 + (Math.random() - 0.5) * 6;
                
                const rotation = (Math.random() - 0.5) * 0.2;
                ctx.save();
                ctx.translate(x, y);
                ctx.rotate(rotation);
                
                ctx.fillStyle = '#333333';
                ctx.fillText(char, 0, 0);
                
                ctx.restore();
            }
        },
        
        refreshCaptcha() {
            this.generateCaptcha();
            this.captchaInput = '';
            this.isValid = false;
            this.emitValues();
        },
        
        validateCaptcha() {
            this.isValid = this.captchaInput.toUpperCase() === this.currentCaptchaCode.toUpperCase();
            return this.isValid;
        },
        
        onFocus() {

        },
        
        onBlur() {
            this.validateCaptcha();
            this.emitValues();
        },
        
        emitValues() {
            const isValid = this.validateCaptcha();
            
            this.$emit("input", {
                order: this.order,
                captchaInput: this.captchaInput,
                captchaCode: this.currentCaptchaCode,
                isValid: isValid,
                timestamp: new Date().toISOString()
            });
            
            this.$emit("validate", {
                fieldId: this.formId + '_' + this.order,
                hasError: this.isRequired && (!this.captchaInput || !isValid)
            });
            
            console.log('🔴 Captcha validation:', {
                input: this.captchaInput,
                code: this.currentCaptchaCode,
                isValid: isValid
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.captcha {
    width: 100%;
    max-width: 400px;
    margin: clamp(0.5rem, 1.5vw, 1rem) auto;
    padding: clamp(0.75rem, 1.8vw, 1rem);
    position: relative;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

    .field-label {
        display: block;
        font-size: clamp(0.9rem, 2vw, 1rem);
        font-weight: 600;
        color: rgba(55, 65, 81, 0.9);
        margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        
        .required-indicator {
            color: #ef4444;
            margin-left: 0.25rem;
            font-weight: 700;
        }
    }

    .captcha-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: clamp(0.75rem, 1.5vw, 1rem);
        
        &.error {
            .captcha-input {
                border-color: #ef4444 !important;
                background-color: #fef2f2 !important;
                box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
            }
        }
    }

    .captcha-preview {
        border-radius: 0.5rem;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        border: 1px solid #e5e7eb;
        background: #f9fafb;
        
        .captcha-canvas {
            display: block;
        }
    }
    
    .captcha-input-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        
        .captcha-input {
            width: 140px;
            padding: 0.75rem;
            font-size: 1rem;
            font-weight: 600;
            text-align: center;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            color: #374151;
            background: white;
            border: 2px solid #e5e7eb;
            border-radius: 0.5rem;
            transition: all 0.3s ease;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
            
            &:focus {
                outline: none;
                border-color: #f26a52;
                box-shadow: 0 0 0 3px rgba(242, 106, 82, 0.1);
            }
            
            &:hover {
                border-color: rgba(242, 106, 82, 0.4);
            }
            
            &::placeholder {
                color: rgba(107, 114, 128, 0.7);
                font-weight: 500;
                letter-spacing: normal;
                text-transform: none;
            }
        }
        
        .refresh-icon-button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 2.75rem;
            height: 2.75rem;
            background: white;
            border: 2px solid #e5e7eb;
            border-radius: 0.5rem;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
            
            &:hover {
                border-color: #f26a52;
                box-shadow: 0 2px 6px rgba(242, 106, 82, 0.15);
            }
            
            .refresh-icon {
                width: 1rem;
                height: 1rem;
                color: #6b7280;
                transition: transform 0.3s ease;
            }
            
            &:hover .refresh-icon {
                transform: rotate(180deg);
                color: #f26a52;
            }
        }
    }

}

.special-captcha-wrapper {
    width: 100%;
    
    .special-captcha-container {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        align-items: center;
        
        .special-captcha-canvas {
            border-radius: 0.5rem;
            border: 1px solid #e2e8f0;
        }
        
        .special-input-group {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            width: 100%;
            max-width: 250px;
            
            .special-captcha-input {
                flex: 1;
                padding: 0.75rem;
                border: 2px solid #e2e8f0;
                border-radius: 0.5rem;
                font-size: 1rem;
                font-weight: 600;
                text-align: center;
                letter-spacing: 0.1em;
                text-transform: uppercase;
                transition: all 0.2s ease;
                
                &:focus {
                    outline: none;
                    border-color: #f26a52;
                    box-shadow: 0 0 0 3px rgba(242, 106, 82, 0.1);
                }
                
                &::placeholder {
                    color: rgba(107, 114, 128, 0.6);
                    font-weight: 500;
                    letter-spacing: normal;
                    text-transform: none;
                }
            }
            
            .special-refresh-button {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 2.5rem;
                height: 2.5rem;
                background: white;
                color: #6b7280;
                border: 2px solid #e5e7eb;
                border-radius: 0.5rem;
                cursor: pointer;
                transition: all 0.2s ease;
                
                &:hover {
                    border-color: #f26a52;
                    color: #f26a52;
                    transform: scale(1.05);
                }
                
                .refresh-icon {
                    width: 1rem;
                    height: 1rem;
                }
            }
        }
    }
}

.captcha-align-left {
    margin-left: 0;
    margin-right: auto;
    
    .field-label {
        text-align: left;
    }
    
    .captcha-container {
        align-items: flex-start;
    }
}

.captcha-align-center {
    margin-left: auto;
    margin-right: auto;
    
    .field-label {
        text-align: center;
    }
    
    .captcha-container {
        align-items: center;
    }
}

.captcha-align-right {
    margin-left: auto;
    margin-right: 0;
    
    .field-label {
        text-align: right;
    }
    
    .captcha-container {
        align-items: flex-end;
    }
}

@media (max-width: 768px) {
    .captcha {
        margin: 0.5rem auto;
        padding: 0.75rem;
        
        .captcha-input-container {
            gap: 0.5rem;
        }
        
        .refresh-icon-button {
            width: 3rem;
            height: 3rem;
        }
    }
    
    .captcha-align-left,
    .captcha-align-right {
        .captcha-container {
            align-items: center;
        }
    }
}

@media (max-width: 480px) {
    .captcha {
        margin: 0.25rem auto;
        padding: 0.5rem;
        
        .captcha-preview {
            align-self: stretch;
            
            .captcha-canvas {
                width: 100%;
                max-width: 200px;
            }
        }
        
        .captcha-input-container {
            flex-direction: column;
            gap: 0.5rem;
            
            .captcha-input {
                width: 100%;
            }
            
            .refresh-icon-button {
                width: 100%;
                max-width: 200px;
            }
        }
    }
}

.captcha {
    will-change: transform;
}

.captcha-preview {
    will-change: box-shadow;
}

.refresh-icon-button {
    will-change: transform, box-shadow;
}

@media (prefers-reduced-motion: reduce) {
    .captcha {
        transition: none;
        
        &:hover {
            transform: none;
        }
    }
    
    .refresh-icon-button {
        transition: none;
        
        &:hover {
            transform: none;
            
            .refresh-icon {
                transform: none;
            }
        }
    }
}
</style>
